/**
 * FullVSL resume-play wiring (FUL-107 / FUL-95 finding IFRAME-4).
 *
 * `contracts/src/resume-schema.ts` already defines the storage contract
 * (`resolveResumeStorage`, `shouldOfferResume`, `createResumeCheckpoint`, …) and
 * it is fully unit-tested, but `FullVslPlayer` never instantiated it — so resume
 * never persisted and RP-1..RP-5 could not pass. This module is the small,
 * dependency-light controller that owns one resume key for the lifetime of a
 * player and exposes the imperatives the player calls on its lifecycle hooks:
 *
 *   - `restore(positionSec)`       called once metadata is known; seeks + returns
 *                                   a prompt decision the player may surface.
 *   - `checkpoint(progress)`        called on progress/pause/seek ticks (throttled).
 *   - `markCtaShown()`              flips the monotonic CTA-shown flag.
 *   - `acceptResume()` / `declineResume()` called when the visitor acts on a prompt.
 *   - `clear()`                     called on completion / media swap.
 *
 * Privacy stance is unchanged from the contract: only the composite key,
 * position/maxWatched/duration, the CTA-shown flag and an updatedAt timestamp
 * are persisted — no PII. The adapter falls back LocalStorage → cookie → memory
 * via `resolveResumeStorage()`, so opaque-origin iframes (where localStorage
 * throws) degrade gracefully to in-session memory instead of crashing.
 */

import type {
  ID,
  ResumeConfig,
  ResumeState,
} from "../../contracts/src/contracts";
import {
  DEFAULT_RESUME_THRESHOLDS,
  type PlayerResumeEvent,
} from "../../contracts/src/contracts";
import {
  createResumeCheckpoint,
  mediaSwapped,
  markCtaShown,
  resumeKey,
  resolveResumeStorage,
  shouldOfferResume,
  shouldShowResumePrompt,
  sourceHash,
  type ResumeKeyInput,
  type ResumeStorage,
} from "../../contracts/src/resume-schema";
import type { PlayerMediaLike } from "./player-media";

/** Inputs the player supplies when constructing the controller. */
export interface ResumeWiringOptions {
  vslId: ID;
  visitorId: string;
  sourceUrl: string;
  config: ResumeConfig;
  /** Optional explicit storage (tests / server-side); defaults to resolved client storage. */
  storage?: ResumeStorage;
  /** Funnel/page scope when a Full Funnel context is present (PRD §11.2). */
  funnelId?: string;
  pageId?: string;
  /** Emit a resume_* player event (player owns seq/identity). */
  emit: (event: PlayerResumeEvent) => void;
}

/** Result of a restore attempt: what (if anything) the player should surface. */
export interface ResumeRestoreResult {
  /** Whether there was usable saved state. */
  hasState: boolean;
  /** Position (seconds) that was restored into the media element. */
  restoredPositionSec: number;
  /** Whether the player should show a resume prompt UI. */
  shouldPrompt: boolean;
  /** Position the prompt offers to resume from. */
  promptFromSec: number;
  /** Reason the decision resolved the way it did (for analytics/tests). */
  reason: ReturnType<typeof shouldOfferResume>["reason"];
}

/** Minimum gap (seconds) between persisted checkpoints (avoids storage churn). */
const CHECKPOINT_MIN_GAP_SEC = 2;

/**
 * Owns resume persistence for one player instance. The player constructs it on
 * init with its stable identity; the controller resolves storage once and reads
 * / writes the same composite key for the whole session.
 */
export class ResumeWiring {
  private readonly keyInput: ResumeKeyInput;
  private readonly key: string;
  private readonly config: ResumeConfig;
  private readonly storage: ResumeStorage;
  private readonly emit: (e: PlayerResumeEvent) => void;
  private lastCheckpointSec = -Infinity;

  constructor(opts: ResumeWiringOptions) {
    this.config = opts.config;
    this.storage = opts.storage ?? resolveResumeStorage().storage;
    this.emit = opts.emit;
    const hash = sourceHash(opts.sourceUrl);
    this.keyInput = {
      videoId: opts.vslId,
      visitorId: opts.visitorId,
      funnelId: opts.funnelId,
      pageId: opts.pageId,
      sourceHash: hash,
    };
    this.key = resumeKey(this.keyInput);
  }

  /**
   * Load saved state and apply it. Seeks the media element when resuming, and
   * returns whether a visible prompt should be shown (only for `mode: "ask"`).
   * On a media swap the stale state is cleared and treated as a first visit.
   */
  restore(media: PlayerMediaLike, durationSec: number): ResumeRestoreResult {
    const state = this.loadFresh(durationSec);
    if (!state) {
      return { hasState: false, restoredPositionSec: 0, shouldPrompt: false, promptFromSec: 0, reason: "no_state" };
    }
    const decision = shouldOfferResume(this.config, state, {
      durationSec,
      thresholds: {
        ...DEFAULT_RESUME_THRESHOLDS,
        minSeconds: Math.max(0, this.config.thresholdSec ?? DEFAULT_RESUME_THRESHOLDS.minSeconds),
      },
    });
    // always_resume: seek silently; ask: seek only after the visitor accepts
    // (so the first frame is not spoiled). For both, restore maxWatched now.
    const silentResume = decision.offer && this.config.mode === "always_resume";
    if (silentResume) {
      this.safeSeek(media, state.positionSec);
    }
    const shouldPrompt = shouldShowResumePrompt(this.config.mode, decision);
    if (shouldPrompt) {
      this.emitResume("resume_offered", state.positionSec, state.maxWatchedSec);
    }
    return {
      hasState: true,
      restoredPositionSec: silentResume ? state.positionSec : 0,
      shouldPrompt,
      promptFromSec: decision.resumeFromSec,
      reason: decision.reason,
    };
  }

  /** Persist a checkpoint if progress has advanced enough since the last write. */
  checkpoint(progress: {
    positionSec: number;
    maxWatchedSec: number;
    durationSec: number;
    ctaShown?: boolean;
  }): void {
    if (Math.abs(progress.positionSec - this.lastCheckpointSec) < CHECKPOINT_MIN_GAP_SEC) return;
    this.lastCheckpointSec = progress.positionSec;
    try {
      const prev = this.storage.get(this.key);
      const next = createResumeCheckpoint(this.keyInput, {
        positionSec: progress.positionSec,
        maxWatchedSec: progress.maxWatchedSec,
        durationSec: progress.durationSec > 0 ? progress.durationSec : undefined,
        ctaShown: progress.ctaShown ?? prev?.ctaShown,
        ctaShownAt: prev?.ctaShownAt,
      });
      this.storage.put(next);
    } catch {
      // storage is best-effort; never break playback over a checkpoint write.
    }
  }

  /** Flip the monotonic CTA-shown flag so a returner re-sees the CTA (FUL-9). */
  markCtaShown(positionSec: number, maxWatchedSec: number): void {
    try {
      const prev = this.storage.get(this.key);
      const base =
        prev ??
        createResumeCheckpoint(this.keyInput, {
          positionSec,
          maxWatchedSec,
        });
      const updated = markCtaShown(base);
      this.storage.put(updated);
    } catch {
      /* best-effort */
    }
  }

  /** Visitor accepted the prompt — seek and record the event. */
  acceptResume(media: PlayerMediaLike): void {
    const state = this.loadFresh(this.mediaDuration(media));
    if (!state) return;
    this.safeSeek(media, state.positionSec);
    this.emitResume("resume_accepted", state.positionSec, state.maxWatchedSec);
  }

  /** Visitor declined the prompt — record the event and clear stale state. */
  declineResume(): void {
    const state = this.loadFresh(0);
    this.emitResume("resume_declined", state?.positionSec ?? 0, state?.maxWatchedSec ?? 0);
  }

  /** Clear saved state (used on completion so a replay starts fresh). */
  clear(): void {
    try {
      this.storage.remove(this.key);
      this.lastCheckpointSec = -Infinity;
    } catch {
      /* best-effort */
    }
  }

  /** Read state, invalidating it when the underlying media was swapped. */
  private loadFresh(durationSec: number): ResumeState | null {
    try {
      const state = this.storage.get(this.key);
      if (!state) return null;
      if (mediaSwapped(state, this.keyInput.sourceHash)) {
        this.storage.remove(this.key);
        return null;
      }
      if (durationSec > 0 && state.durationSec == null) state.durationSec = durationSec;
      return state;
    } catch {
      return null;
    }
  }

  private safeSeek(media: PlayerMediaLike, sec: number): void {
    try {
      if (Number.isFinite(sec) && sec > 0) media.currentTime = sec;
    } catch {
      /* seeking can throw before metadata is ready; non-fatal */
    }
  }

  private mediaDuration(media: PlayerMediaLike): number {
    return Number.isFinite(media.duration) ? media.duration : 0;
  }

  private emitResume(
    name: "resume_offered" | "resume_accepted" | "resume_declined",
    positionSec: number,
    maxWatchedSec: number,
  ): void {
    this.emit({
      seq: 0,
      name,
      clientTs: new Date().toISOString(),
      vslId: "",
      sessionId: "",
      visitorId: "",
      positionSec,
      maxWatchedSec,
    });
  }
}

/** Default resume config used when the player is constructed without one. */
export const DEFAULT_RESUME_CONFIG: ResumeConfig = { mode: "ask" };

export { DEFAULT_RESUME_THRESHOLDS };
